/**
 * Account-number normalisation for the standalone "check name" tool.
 *
 * Operators paste mobile numbers in whatever shape they arrived in — from a ticket, a WhatsApp
 * message, a spreadsheet — so the field accepts all of them and normalises rather than rejecting.
 * The payconnect name-check API wants digits only, including the country code and with no leading
 * "+", e.g. 233505922888.
 *
 * The normalised value is shown back to the operator under the field, so a mangled paste is
 * visible before the check runs rather than being blamed on the account.
 */

/**
 * Normalise a mobile number to the digits-only, country-code-prefixed form payconnect expects.
 *
 * Handles, for a dial code of "+233":
 *   0505922888        → 233505922888   (national trunk 0 dropped)
 *   505922888         → 233505922888   (bare national number)
 *   +233505922888     → 233505922888   (plus stripped)
 *   233 505 922 888   → 233505922888   (separators stripped)
 *   +233 (0)505922888 → 233505922888   (trunk 0 after the code dropped)
 *   +233233505922888  → 233505922888   (duplicated country code collapsed)
 *
 * @param {string} raw       what the operator typed or pasted
 * @param {string} dialCode  the selected country's dial code, with or without "+" (e.g. "+233")
 * @returns {string} digits only, or "" when there is nothing usable
 */
export function normaliseMobileNumber(raw, dialCode) {
  let digits = onlyDigits(raw);
  if (!digits) return "";

  const code = onlyDigits(dialCode);
  if (!code) return digits;

  // "00" is the international dialling prefix. It has to go FIRST: leave it on and the country
  // code that follows it is read as a national number, so the code gets prefixed a second time
  // and the result is silently wrong (00233505922888 → 233233505922888).
  if (digits.startsWith("00")) {
    digits = digits.slice(2);
  }

  // Collapse a country code the operator supplied more than once (a classic copy-paste artefact).
  while (digits.startsWith(code + code)) {
    digits = digits.slice(code.length);
  }

  if (digits.startsWith(code)) {
    // Already prefixed. A trunk 0 between the code and the subscriber number is not part of the
    // international format — "+233 (0)50..." is written that way but dialled without the 0.
    const national = digits.slice(code.length);
    return code + stripTrunkZero(national);
  }

  return code + stripTrunkZero(digits);
}

/**
 * Normalise a bank account number: digits only. Bank account numbers carry no country code and no
 * trunk prefix, so nothing is added or removed beyond separators.
 *
 * @param {string} raw
 * @returns {string}
 */
export function normaliseBankAccountNumber(raw) {
  return onlyDigits(raw);
}

/**
 * Normalise for whichever payout type is selected.
 *
 * @param {string} raw
 * @param {"Mobile Money"|"Bank Deposit"} payoutType
 * @param {string} dialCode  only used for Mobile Money
 * @returns {string}
 */
export function normaliseAccountNumber(raw, payoutType, dialCode) {
  return payoutType === "Bank Deposit"
    ? normaliseBankAccountNumber(raw)
    : normaliseMobileNumber(raw, dialCode);
}

function onlyDigits(value) {
  if (value === null || value === undefined) return "";
  return String(value).replace(/\D/g, "");
}

function stripTrunkZero(national) {
  // A trunk prefix is exactly one leading 0 — never a greedy /^0+/, which would also eat the
  // first digit of a subscriber number that legitimately begins with 0.
  return national.startsWith("0") ? national.slice(1) : national;
}
